import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

interface LeaveState {
  leaveTypes: any;
  leaveAbsenceTypes: any;
  leaveRequestById: any;
  syncLeaves: any;
  leaveAttachment: any;
  leaveCalendar: any;
  leaveDropDown: any;
  allLeaves: any;
  allEmployeeLeaves: any;
  teamLeaveCalendar: any;
  leaveTimeAccounts: any;
  createLeaveResponse: any;
  empLeaveFileAttachments: any;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action: string | null;
}

const initialState: LeaveState = {
  leaveTypes: null,
  leaveAbsenceTypes: null,
  leaveRequestById: null,
  syncLeaves: null,
  leaveAttachment: null,
  empLeaveFileAttachments: null,
  leaveCalendar: null,
  leaveDropDown: null,
  allLeaves: null,
  allEmployeeLeaves: null,
  teamLeaveCalendar: null,
  leaveTimeAccounts: null,
  createLeaveResponse: null,
  loading: "idle",
  error: null,
  action: null,
};

export const getLeaveTypes: any = createAsyncThunk(
  "fetch/leaveTypes",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.LEAVE_TYPES, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const createLeaveRequest: any = createAsyncThunk(
  "leave/createLeaveRequest",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.CREATE_LEAVE_REQUEST, payload);
      if (response && typeof response === "object") {
        return {
          ...(response.data || {}),
          ...response,
          raw: response,
          data: response.data || response,
        };
      }
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getEmpLeaveAbsenceType: any = createAsyncThunk(
  "leave/getEmpLeaveAbsenceType",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.EMP_LEAVE_ABSENCE_TYPE, payload);
      return response?.data || response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getLeaveRequestById: any = createAsyncThunk(
  "leave/getLeaveRequestById",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.GET_LEAVE_REQUEST_BY_ID, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const syncEmployeeLeaves: any = createAsyncThunk(
  "leave/syncEmployeeLeaves",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.SYNC_EMPLOYEE_LEAVES, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const syncDeletedEmployeeLeaves: any = createAsyncThunk(
  "leave/syncDeletedEmployeeLeaves",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.SYNC_DELETED_EMPLOYEE_LEAVES, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const uploadLeaveAttachment: any = createAsyncThunk(
  "leave/uploadLeaveAttachment",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.UPLOAD_LEAVE_ATTACHMENT, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getEmpLeaveCalendar: any = createAsyncThunk(
  "leave/getEmpLeaveCalendar",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.EMP_LEAVE_CALENDAR, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getLeaveDropDown: any = createAsyncThunk(
  "leave/getLeaveDropDown",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.GET_DROP_DOWN, payload || {});
      return response?.data || response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getAllLeaves: any = createAsyncThunk(
  "leave/getAllLeaves",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.GET_ALL_LEAVES, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getAllEmployeeLeaves: any = createAsyncThunk(
  "leave/getAllEmployeeLeaves",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.get(API_URLS.LEAVE.ALL_EMPLOYEE_LEAVES, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getTeamLeaveCalendar: any = createAsyncThunk(
  "leave/getTeamLeaveCalendar",
  async (payload: any, thunkAPI) => {
    try {
      // The emp-leave-calendar API returns BOTH the requester (IsRequester: true)
      // AND all team members (IsRequester: false, unique RowNumber per member).
      // No separate team-calendar endpoint is needed.
      const response = await Api.post(API_URLS.LEAVE.EMP_LEAVE_CALENDAR, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getEmpLeaveTimeAccounts: any = createAsyncThunk(
  "leave/getEmpLeaveTimeAccounts",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.EMP_LEAVE_TIME_ACCOUNTS, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getEmpLeaveFileAttachment: any = createAsyncThunk(
  "leave/getEmpLeaveFileAttachment",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.LEAVE.EMP_LEAVE_FILE_ATTACHMENT, payload);
      return response?.data || response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const leaveSlice = createSlice({
  name: "leave",
  initialState: initialState,
  reducers: {
    resetAll: (state: any) => {
      state.loading = "idle";
      state.error = null;
      state.leaveTypes = null;
      state.leaveAbsenceTypes = null;
      state.leaveRequestById = null;
      state.syncLeaves = null;
      state.leaveAttachment = null;
      state.leaveCalendar = null;
      state.leaveDropDown = null;
      state.allLeaves = null;
      state.allEmployeeLeaves = null;
      state.teamLeaveCalendar = null;
      state.leaveTimeAccounts = null;
      state.createLeaveResponse = null;
      state.action = null;
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: any, actionType: string) => {
      state.loading = "pending";
      state.error = null;
      state.action = actionType;
    };
    
    const handleRejected = (state: any, action: any, actionType: string) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = actionType;
    };

    // getLeaveTypes
    builder.addCase(getLeaveTypes.pending, (state) => handlePending(state, "leave-types"));
    builder.addCase(getLeaveTypes.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.leaveTypes = action.payload;
      state.error = null;
      state.action = "leave-types";
    });
    builder.addCase(getLeaveTypes.rejected, (state, action) => handleRejected(state, action, "leave-types"));

    // createLeaveRequest
    builder.addCase(createLeaveRequest.pending, (state) => handlePending(state, "create-leave-request"));
    builder.addCase(createLeaveRequest.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.createLeaveResponse = action.payload;
      state.error = null;
      state.action = "create-leave-request";
    });
    builder.addCase(createLeaveRequest.rejected, (state, action) => handleRejected(state, action, "create-leave-request"));

    // getEmpLeaveAbsenceType
    builder.addCase(getEmpLeaveAbsenceType.pending, (state) => handlePending(state, "emp-leave-absence-type"));
    builder.addCase(getEmpLeaveAbsenceType.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.leaveAbsenceTypes = action.payload;
      state.error = null;
      state.action = "emp-leave-absence-type";
    });
    builder.addCase(getEmpLeaveAbsenceType.rejected, (state, action) => handleRejected(state, action, "emp-leave-absence-type"));

    // getLeaveRequestById
    builder.addCase(getLeaveRequestById.pending, (state) => handlePending(state, "leave-request-by-id"));
    builder.addCase(getLeaveRequestById.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.leaveRequestById = action.payload;
      state.error = null;
      state.action = "leave-request-by-id";
    });
    builder.addCase(getLeaveRequestById.rejected, (state, action) => handleRejected(state, action, "leave-request-by-id"));

    // syncEmployeeLeaves
    builder.addCase(syncEmployeeLeaves.pending, (state) => handlePending(state, "sync-employee-leaves"));
    builder.addCase(syncEmployeeLeaves.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.syncLeaves = action.payload;
      state.error = null;
      state.action = "sync-employee-leaves";
    });
    builder.addCase(syncEmployeeLeaves.rejected, (state, action) => handleRejected(state, action, "sync-employee-leaves"));

    // uploadLeaveAttachment
    builder.addCase(uploadLeaveAttachment.pending, (state) => handlePending(state, "upload-leave-attachment"));
    builder.addCase(uploadLeaveAttachment.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.leaveAttachment = action.payload;
      state.error = null;
      state.action = "upload-leave-attachment";
    });
    builder.addCase(uploadLeaveAttachment.rejected, (state, action) => handleRejected(state, action, "upload-leave-attachment"));

    // getTeamLeaveCalendar
    builder.addCase(getTeamLeaveCalendar.pending, (state) => handlePending(state, "get-team-leave-calendar"));
    builder.addCase(getTeamLeaveCalendar.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.teamLeaveCalendar = action.payload;
      state.error = null;
      state.action = "get-team-leave-calendar";
    });
    builder.addCase(getTeamLeaveCalendar.rejected, (state, action) => handleRejected(state, action, "get-team-leave-calendar"));

    // getEmpLeaveCalendar
    builder.addCase(getEmpLeaveCalendar.pending, (state) => handlePending(state, "emp-leave-calendar"));
    builder.addCase(getEmpLeaveCalendar.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.leaveCalendar = action.payload;
      state.error = null;
      state.action = "emp-leave-calendar";
    });
    builder.addCase(getEmpLeaveCalendar.rejected, (state, action) => handleRejected(state, action, "emp-leave-calendar"));

    // getLeaveDropDown
    builder.addCase(getLeaveDropDown.pending, (state) => handlePending(state, "get-leave-drop-down"));
    builder.addCase(getLeaveDropDown.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.leaveDropDown = action.payload;
      state.error = null;
      state.action = "get-leave-drop-down";
    });
    builder.addCase(getLeaveDropDown.rejected, (state, action) => handleRejected(state, action, "get-leave-drop-down"));

    // getAllLeaves
    builder.addCase(getAllLeaves.pending, (state) => handlePending(state, "get-all-leaves"));
    builder.addCase(getAllLeaves.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.allLeaves = action.payload;
      state.error = null;
      state.action = "get-all-leaves";
    });
    builder.addCase(getAllLeaves.rejected, (state, action) => handleRejected(state, action, "get-all-leaves"));

    // getAllEmployeeLeaves
    builder.addCase(getAllEmployeeLeaves.pending, (state) => handlePending(state, "all-employee-leaves"));
    builder.addCase(getAllEmployeeLeaves.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.allEmployeeLeaves = action.payload;
      state.error = null;
      state.action = "all-employee-leaves";
    });
    builder.addCase(getAllEmployeeLeaves.rejected, (state, action) => handleRejected(state, action, "all-employee-leaves"));

    // getEmpLeaveTimeAccounts
    builder.addCase(getEmpLeaveTimeAccounts.pending, (state) => handlePending(state, "get-emp-leave-time-accounts"));
    builder.addCase(getEmpLeaveTimeAccounts.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.leaveTimeAccounts = action.payload;
      state.error = null;
      state.action = "get-emp-leave-time-accounts";
    });
    builder.addCase(getEmpLeaveTimeAccounts.rejected, (state, action) => handleRejected(state, action, "get-emp-leave-time-accounts"));

    // getEmpLeaveFileAttachment
    builder.addCase(getEmpLeaveFileAttachment.pending, (state) => handlePending(state, "emp-leave-file-attachment"));
    builder.addCase(getEmpLeaveFileAttachment.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.empLeaveFileAttachments = action.payload;
      state.error = null;
      state.action = "emp-leave-file-attachment";
    });
    builder.addCase(getEmpLeaveFileAttachment.rejected, (state, action) => handleRejected(state, action, "emp-leave-file-attachment"));
  },
});

export const { resetAll } = leaveSlice.actions;
export default leaveSlice.reducer;
